import type { MouseEvent, VoidFunctionComponent } from 'react';

import { renderRevision } from '../../../../../../../shared/helpers/renderLocator';
import { Badge, BadgeColors, BUTTON_TYPE, icons } from '../../../../../generic';
import Button from '../../../../../generic/Button';
import TooltipIcon from '../../../../../generic/TooltipIcon';
import type Locator from '../../../../../../../shared/Locator';

import styles from '../DependencyRow.module.less';

export type DependencyTitleProps = {
  title: string;
  revision: Locator;
  version?: string;
  depth: number;
  layerDepth?: number;
  isManual: boolean;
  isBlocked: boolean;
  onClick: (e: MouseEvent<HTMLElement>) => void;
};

const BlockedBadge: VoidFunctionComponent = () => (
  <div className={styles.infoBadge}>
    <Badge color={BadgeColors.Red}>Blocked</Badge>
  </div>
);

const ManualBadge: VoidFunctionComponent = () => (
  <div className={styles.infoBadge}>
    <Badge color={BadgeColors.LightBlue}>Manual Dependency</Badge>
  </div>
);

type DepthIconProps = {
  isDirect: boolean;
};

const DepthIcon: VoidFunctionComponent<DepthIconProps> = ({ isDirect }) => (
  <span className={styles.depthIcon}>
    <TooltipIcon
      icon={isDirect ? icons.box : icons.sitemap}
      tooltip={`Depth: ${isDirect ? 'Direct' : 'Transitive'} Dependency`}
      id={`depth-icon-${isDirect ? 'direct' : 'transitive'}`}
    />
  </span>
);

type LayerDepthIconProps = {
  isBase: boolean;
};

const LayerDepthIcon: VoidFunctionComponent<LayerDepthIconProps> = ({ isBase }) => (
  <TooltipIcon
    icon={isBase ? icons.sendBackward : icons.layerGroup}
    tooltip={`Layer: ${isBase ? 'Base' : 'Other'} Layer`}
    id={`depth-icon-${isBase ? 'base' : 'other'}`}
  />
);

const DependencyTitle: VoidFunctionComponent<DependencyTitleProps> = ({
  title,
  revision,
  depth,
  layerDepth,
  isManual,
  isBlocked,
  version,
  onClick,
}) => {
  const versionTag = version || renderRevision(revision.toParts());

  return (
    <Button type={BUTTON_TYPE.TEXT} onClick={onClick}>
      <div className={styles.DependencyInfoTitle}>
        {/* Package Info */}
        <span className={styles.title}>{title}</span>
        <div className={styles.revision}>
          (<span>{versionTag}</span>)
        </div>
        <DepthIcon isDirect={depth <= 1} />
        {layerDepth !== undefined && <LayerDepthIcon isBase={layerDepth === 0} />}
        {isBlocked && <BlockedBadge />}
        {isManual && <ManualBadge />}
      </div>
    </Button>
  );
};

export default DependencyTitle;
